import React, { useEffect, useState } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	CircularProgress,
	Alert,
	Typography,
	Box,
	Tabs,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Link,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Stack
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { AuditLogEntry } from "../../models/auditLog";
import { getAuditLogsByItemCode } from "../../firebase/auditLog/auditLogRepository";
import { AuditLogTable } from "./AuditLogTable";
import { buildQuantityChangeRows } from "./quantityChangeSummary";

interface ItemAuditLogDialogProps {
	open: boolean;
	onClose: () => void;
	itemName: string;
	itemCode: string;
}

export const ItemAuditLogDialog: React.FC<ItemAuditLogDialogProps> = ({
	open,
	onClose,
	itemName,
	itemCode
}) => {
	const theme = useTheme();
	const isDarkMode = theme.palette.mode === "dark";
	const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [activeTab, setActiveTab] = useState<"history" | "quantity">(
		"quantity"
	);

	useEffect(() => {
		const fetchLogs = async () => {
			if (!open || !itemCode) {
				return;
			}

			setLoading(true);
			setError(null);
			try {
				const logs = await getAuditLogsByItemCode(itemCode);
				setAuditLogs(logs);
			} catch (err) {
				setError("Failed to load audit logs for this item.");
				console.error(err);
			} finally {
				setLoading(false);
			}
		};

		void fetchLogs();
	}, [open, itemCode]);

	const quantityRows = buildQuantityChangeRows(auditLogs, itemCode);
	const [changeFilter, setChangeFilter] = useState<
		"all" | "increase" | "decrease" | "zero"
	>("all");

	const filteredRows = quantityRows.filter((r) => {
		if (changeFilter === "all") return true;
		const val = r.changeQuantity ?? 0;
		if (changeFilter === "increase") return val > 0;
		if (changeFilter === "decrease") return val < 0;
		if (changeFilter === "zero") return val === 0;
		return true;
	});

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="lg"
			fullWidth
			PaperProps={{
				sx: {
					bgcolor: theme.palette.background.paper,
					color: theme.palette.text.primary
				}
			}}
		>
			<DialogTitle
				sx={{
					borderBottom: `1px solid ${theme.palette.divider}`,
					bgcolor: theme.palette.background.paper,
					color: theme.palette.text.primary
				}}
			>
				Audit Logs for Item
				<Typography
					variant="subtitle2"
					sx={{ mt: 1, color: "text.secondary" }}
				>
					{itemName}
					{itemCode ? ` (${itemCode})` : ""}
				</Typography>
			</DialogTitle>
			<DialogContent
				dividers
				sx={{
					bgcolor: theme.palette.background.default,
					color: theme.palette.text.primary
				}}
			>
				{loading ? (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							py: 4
						}}
					>
						<CircularProgress />
					</Box>
				) : error ? (
					<Alert
						severity="error"
						sx={{
							bgcolor: alpha(
								theme.palette.error.main,
								isDarkMode ? 0.14 : 0.08
							),
							color: theme.palette.text.primary
						}}
					>
						{error}
					</Alert>
				) : auditLogs.length === 0 ? (
					<Alert
						severity="info"
						sx={{
							bgcolor: alpha(
								theme.palette.info.main,
								isDarkMode ? 0.14 : 0.08
							),
							color: theme.palette.text.primary
						}}
					>
						No audit logs found for this item.
					</Alert>
				) : (
					<>
						<Box
							sx={{
								borderBottom: 1,
								borderColor: "divider",
								mb: 2
							}}
						>
							<Tabs
								value={activeTab}
								onChange={(_, value) =>
									setActiveTab(
										value as "history" | "quantity"
									)
								}
								aria-label="audit log tabs"
							>
								<Tab
									label="Quantity Changes"
									value="quantity"
								/>
								<Tab label="Audit History" value="history" />
							</Tabs>
						</Box>

						{activeTab === "history" ? (
							<AuditLogTable auditLogs={auditLogs} />
						) : (
							<>
								{quantityRows.length === 0 ? (
									<Alert severity="info">
										No quantity-change entries found for
										this item.
									</Alert>
								) : (
									<Paper
										elevation={1}
										sx={{
											p: 2,
											mb: 2,
											bgcolor:
												theme.palette.background.paper,
											color: theme.palette.text.primary,
											border: `1px solid ${theme.palette.divider}`,
											borderRadius: 2
										}}
									>
										<Stack
											direction={{
												xs: "column",
												sm: "row"
											}}
											justifyContent="space-between"
											alignItems="center"
											sx={{ mb: 1 }}
										>
											<Typography variant="subtitle1">
												Quantity Change Summary
											</Typography>
											<FormControl
												size="small"
												sx={{ minWidth: 160 }}
											>
												<InputLabel id="change-filter-label">
													Filter
												</InputLabel>
												<Select
													labelId="change-filter-label"
													value={changeFilter}
													label="Filter"
													onChange={(e) =>
														setChangeFilter(
															e.target
																.value as any
														)
													}
												>
													<MenuItem value="all">
														All
													</MenuItem>
													<MenuItem value="increase">
														Increase (+)
													</MenuItem>
													<MenuItem value="decrease">
														Decrease (-)
													</MenuItem>
													<MenuItem value="zero">
														No Change
													</MenuItem>
												</Select>
											</FormControl>
										</Stack>
										<TableContainer
											sx={{
												maxHeight: 520,
												borderRadius: 1
											}}
										>
											<Table stickyHeader size="small">
												<TableHead>
													<TableRow>
														<TableCell
															sx={{
																fontWeight: 700,
																textTransform:
																	"uppercase",
																fontSize:
																	"0.8rem"
															}}
														>
															Timestamp
														</TableCell>
														<TableCell
															sx={{
																fontWeight: 700,
																textTransform:
																	"uppercase",
																fontSize:
																	"0.8rem"
															}}
														>
															Entity
														</TableCell>
														<TableCell
															sx={{
																fontWeight: 700,
																textTransform:
																	"uppercase",
																fontSize:
																	"0.8rem"
															}}
														>
															Operation
														</TableCell>
														<TableCell
															sx={{
																fontWeight: 700,
																textTransform:
																	"uppercase",
																fontSize:
																	"0.8rem"
															}}
														>
															Invoice #
														</TableCell>
														<TableCell
															sx={{
																fontWeight: 700,
																textTransform:
																	"uppercase",
																fontSize:
																	"0.8rem"
															}}
														>
															Before
														</TableCell>
														<TableCell
															sx={{
																fontWeight: 700,
																textTransform:
																	"uppercase",
																fontSize:
																	"0.8rem"
															}}
														>
															After
														</TableCell>
														<TableCell
															sx={{
																fontWeight: 700,
																textTransform:
																	"uppercase",
																fontSize:
																	"0.8rem"
															}}
														>
															Change
														</TableCell>
														<TableCell
															sx={{
																fontWeight: 700,
																textTransform:
																	"uppercase",
																fontSize:
																	"0.8rem"
															}}
														>
															User
														</TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{filteredRows.map((row) => (
														<TableRow
															key={row.id}
															hover
														>
															<TableCell>
																{new Date(
																	row.timestamp
																).toLocaleString()}
															</TableCell>
															<TableCell>
																{row.entityType}
															</TableCell>
															<TableCell>
																{row.operation}
															</TableCell>
															<TableCell>
																{row.entityType ===
																	"Invoice" ||
																row.entityType ===
																	"Return" ? (
																	row.invoiceNumber ? (
																		<Link
																			href={
																				"/invoice?invoiceNumber=" +
																				encodeURIComponent(
																					row.invoiceNumber as string
																				)
																			}
																			target="_blank"
																			rel="noopener noreferrer"
																			underline="hover"
																			color="primary"
																			style={{
																				cursor: "pointer"
																			}}
																		>
																			{
																				row.invoiceNumber
																			}
																		</Link>
																	) : (
																		row.itemName ||
																		row.itemCode ||
																		"-"
																	)
																) : (
																	row.itemCode ||
																	row.itemName ||
																	"-"
																)}
															</TableCell>
															<TableCell>
																{row.beforeQuantity ??
																	"-"}
															</TableCell>
															<TableCell>
																{row.afterQuantity ??
																	"-"}
															</TableCell>
															<TableCell
																sx={{
																	color: (
																		theme
																	) =>
																		row.changeQuantity &&
																		row.changeQuantity >
																			0
																			? theme
																					.palette
																					.success
																					.main
																			: row.changeQuantity &&
																				  row.changeQuantity <
																						0
																				? theme
																						.palette
																						.error
																						.main
																				: theme
																						.palette
																						.text
																						.primary,
																	fontWeight: 700
																}}
															>
																{typeof row.changeQuantity ===
																"number"
																	? row.changeQuantity >
																		0
																		? "+" +
																			row.changeQuantity
																		: String(
																				row.changeQuantity
																			)
																	: "-"}
															</TableCell>
															<TableCell>
																{row.username}
															</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										</TableContainer>
									</Paper>
								)}
							</>
						)}
					</>
				)}
			</DialogContent>
			<DialogActions
				sx={{
					borderTop: `1px solid ${theme.palette.divider}`,
					px: 3,
					py: 1.5,
					bgcolor: theme.palette.background.paper
				}}
			>
				<Button onClick={onClose} variant="contained">
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
};
